import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  captureApiContext,
  CommunicationService,
  HistoryAction,
  HistoryItem,
} from '../../services/communication-service/communication.service';
import { CsvService } from '../../services/csv-service/csv.service';
import { LocalHistoryService } from '../../services/local-history-service/local-history.service';
import { ModalService } from '../../services/modal/modal.service';
import { SettingsService } from '../../services/setting-service/setting.service';

@Component({
  selector: 'app-communications-history',
  standalone: false,
  templateUrl: './communications-history.html',
  styleUrl: './communications-history.scss',
})
export class CommunicationsHistory implements OnInit, OnDestroy {
  loading: boolean = false;
  filtersExpanded: boolean = true;

  acoes: HistoryAction[] = [];
  private queryRevision = 0;
  private listContext: (() => boolean) | null = null;

  private requireCurrentContext(revision = this.queryRevision): boolean {
    if (revision !== this.queryRevision) return false;
    if (this.listContext?.()) return true;
    this.queryRevision++;
    this.listContext = null;
    this.acoes = [];
    this.detalhes.clear();
    this.expandidas.clear();
    this.carregandoDetalhe.clear();
    this.loading = false;
    this.modalService.error(
      'A sessão ou a API mudou. Pesquise o histórico novamente.',
      'Recarregue o histórico',
    );
    return false;
  }

  ngOnDestroy() {
    this.queryRevision++;
  }

  /** Detalhes já carregados, indexados por id da ação. */
  detalhes = new Map<number, HistoryItem[]>();
  carregandoDetalhe = new Set<number>();
  expandidas = new Set<number>();

  dataInicio = new FormControl();
  dataFim = new FormControl();
  tipoControl = new FormControl('');
  administradorControl = new FormControl('');
  buscaControl = new FormControl('');
  /** Nome dado ao disparo pontual na tela de Disparos. */
  nomeControl = new FormControl('');

  tipoOptions = [
    { value: '', description: 'Todos' },
    { value: 'processamento', description: 'Processamento massivo' },
    { value: 'disparo', description: 'Disparo pontual' },
  ];

  constructor(
    private communicationService: CommunicationService,
    private csvService: CsvService,
    private localHistoryService: LocalHistoryService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    public settingsService: SettingsService,
  ) {
    this.settingsService.title = 'Histórico de Comunicações';
  }

  /**
   * `true` enquanto as ações vierem do navegador em vez da API. Enquanto for
   * assim, a tela precisa deixar isso claro — o registro não é auditoria.
   */
  get modoLocal(): boolean {
    return this.communicationService.modoLocal;
  }

  async ngOnInit() {
    const hoje = moment();
    this.dataInicio.setValue(hoje.clone().subtract(90, 'days').toDate());
    this.dataFim.setValue(hoje.toDate());

    // Busca reversa: a tela de Pedidos manda o nº do pedido por query param
    // para responder "quem mexeu neste pedido?".
    const pedido = this.route.snapshot.queryParamMap.get('pedido');
    if (pedido) {
      this.buscaControl.setValue(pedido);
    }

    await this.carregar();
  }

  async carregar() {
    const revision = ++this.queryRevision;
    this.listContext = captureApiContext(this.settingsService);
    this.acoes = [];
    this.loading = true;
    // console.log('buscando comunicacoes...')
    this.detalhes.clear();
    this.expandidas.clear();
    this.carregandoDetalhe.clear();

    const filtros: any = {
      dataInicio: this.dataInicio.value
        ? moment(this.dataInicio.value).format('YYYY-MM-DD')
        : undefined,
      dataFim: this.dataFim.value ? moment(this.dataFim.value).format('YYYY-MM-DD') : undefined,
      tipo: this.tipoControl.value || undefined,
      administrador: this.administradorControl.value || undefined,
      numero_pedido: this.buscaControl.value || undefined,
      nome: this.nomeControl.value || undefined,
    };

    try {
      const result = await this.communicationService.getHistory(filtros);
      if (!this.requireCurrentContext(revision)) return;
      this.acoes = result;
    } catch (error: any) {
      if (!this.requireCurrentContext(revision)) return;
      this.modalService.error(
        error?.error?.message || error?.message || 'Não foi possível carregar o histórico.',
        'Erro ao carregar',
      );
      this.acoes = [];
    } finally {
      if (revision === this.queryRevision) this.loading = false;
    }
  }

  limparFiltros() {
    this.tipoControl.setValue('');
    this.administradorControl.setValue('');
    this.buscaControl.setValue('');
    this.nomeControl.setValue('');
    this.carregar();
  }

  toggleFilters() {
    this.filtersExpanded = !this.filtersExpanded;
  }

  /** Abre ou fecha a camada de detalhe, carregando sob demanda. */
  async alternarDetalhe(acao: HistoryAction) {
    if (!this.requireCurrentContext() || !this.acoes.includes(acao)) return;
    if (this.expandidas.has(acao.id_acao)) {
      this.expandidas.delete(acao.id_acao);
      return;
    }

    this.expandidas.add(acao.id_acao);

    if (this.detalhes.has(acao.id_acao) || this.carregandoDetalhe.has(acao.id_acao)) {
      return;
    }

    const revision = this.queryRevision;
    this.carregandoDetalhe.add(acao.id_acao);

    try {
      const itens = Array.isArray(acao.itens)
        ? acao.itens
        : await this.communicationService.getHistoryDetails(acao.id_acao);
      if (!this.requireCurrentContext(revision)) return;
      this.detalhes.set(acao.id_acao, itens);
    } catch (error: any) {
      if (!this.requireCurrentContext(revision)) return;
      this.modalService.error(
        error?.error?.message || 'Não foi possível carregar os registros desta ação.',
        'Erro ao carregar',
      );
      this.expandidas.delete(acao.id_acao);
    } finally {
      if (revision === this.queryRevision) this.carregandoDetalhe.delete(acao.id_acao);
    }
  }

  itensDe(idAcao: number): HistoryItem[] {
    return this.detalhes.get(idAcao) ?? [];
  }

  /** Apaga o registro local. Some junto com o modo local, quando a API existir. */
  limparHistoricoLocal() {
    this.modalService.custom({
      type: 'warning',
      title: 'Limpar registro local',
      message:
        'Todas as ações registradas neste navegador serão apagadas.\n' +
        'Os pedidos e os status atuais não mudam — só o registro é perdido.',
      showCloseButton: true,
      listButtons: [
        { textButton: 'Cancelar', fill: 'outline' },
        {
          textButton: 'Apagar',
          fill: 'solid',
          action: () => {
            this.localHistoryService.limpar();
            this.carregar();
          },
        },
      ],
    });
  }

  exportar() {
    if (!this.requireCurrentContext()) return;
    if (this.loading || !this.acoes.length) {
      return;
    }

    const headers = [
      'data',
      'administrador',
      'email',
      'tipo',
      'nome',
      'acao',
      'assunto',
      'registros',
      'template',
      'sucessos',
      'falhas',
    ];

    const rows = this.acoes.map((acao) => ({
      data: this.formatDateTime(acao.data),
      administrador: acao.administrador.nome,
      email: acao.administrador.email,
      tipo: this.rotuloTipo(acao.tipo),
      nome: acao.nome ?? '',
      acao: acao.acao,
      assunto: acao.assunto ?? '',
      registros: String(acao.quantidade),
      template: acao.template ?? 'Nenhum',
      sucessos: String(acao.sucessos),
      falhas: String(acao.falhas),
    }));

    this.csvService.download(
      `historico-comunicacoes-${moment().format('YYYY-MM-DD-HHmm')}.csv`,
      this.csvService.build(headers, rows),
    );
  }

  formatDateTime(data: string): string {
    if (!data) {
      return '';
    }
    const date = moment(data, 'DD/MM/YYYY HH:mm', true);
    return (date.isValid() ? date : moment(data, moment.ISO_8601, true))
      .locale('pt-br')
      .format('DD/MM/YYYY HH:mm');
  }

  rotuloTipo(tipo: string): string {
    return ['processamento', 'PROCESSAMENTO MASSIVO'].includes(tipo)
      ? 'Processamento massivo'
      : 'Disparo pontual';
  }

  motivoNaoEnvio(item: { estado?: string; motivo_falha: string | null }): string {
    if (item.estado === 'sem_alteracao') return 'Não enviado: pedido já estava nesse status.';
    if (item.estado === 'sem_comunicacao') return 'Envio não solicitado nesta ação.';
    if (item.estado === 'sem_template') return 'Não enviado: status sem template configurado.';
    return item.motivo_falha || 'Não enviado';
  }

  getStatusClass(status: string): string {
    if (!status) {
      return 'status-default';
    }

    switch (status.toLowerCase().trim()) {
      case 'pendente':
        return 'status-pendente';
      case 'aprovado':
        return 'status-aprovado';
      case 'em processamento':
        return 'status-processamento';
      case 'enviado':
        return 'status-enviado';
      case 'entregue':
        return 'status-entregue';
      case 'cancelado':
        return 'status-cancelado';
      case 'cancelado pelo programa':
        return 'status-cancelado-programa';
      case 'devolvido':
        return 'status-devolvido';
      default:
        return 'status-default';
    }
  }
}
